import { EmptyState, Mono, Table, Tag, Td, Th } from "./ui";
import { formatCount } from "../lib/money";
import { formatDate } from "../lib/format";
import type { ClickSource } from "../lib/types";
import { useTranslation } from "react-i18next";

/**
 * Where the clicks came from.
 *
 * Shared by the campaign screen and the traffic screen, which read it for the
 * same reason and differ only in whether a link column is worth showing.
 *
 * ---------------------------------------------------------------------------
 * The share column is doing the work
 * ---------------------------------------------------------------------------
 * A count on its own invites the wrong comparison: 200 clicks is a lot or a
 * little depending on the total, and an affiliate scanning ten rows should not
 * have to add them up. The share is against the rows shown, which is what the
 * bar is drawn from too.
 *
 * ---------------------------------------------------------------------------
 * What this cannot tell you, said plainly
 * ---------------------------------------------------------------------------
 * **Which source converted.** A signup arrives from the platform with a code
 * and no visitor identity, so there is nothing to join a conversion back to a
 * referrer host on — see `affiliate-insights.ts`. Guessing would mean crediting
 * the most recent click on that link, which would be wrong often and would look
 * authoritative.
 *
 * So the honest reading is: this says where traffic *came from*, and the
 * campaign's funnel says whether traffic from that link *worked*. An affiliate
 * who wants both answers at once makes a link per placement, which the screens
 * say rather than leaving them to infer.
 */
export function SourceTable({
  sources,
  showLink = false,
}: {
  sources: ClickSource[];
  /** On the traffic screen, where rows span campaigns. Noise on one campaign. */
  showLink?: boolean;
}) {
  const { t } = useTranslation("traffic");
  if (sources.length === 0) {
    return (
      <EmptyState
        title={t("sources.empty")}
        description={t("sources.emptyDescription")}
      />
    );
  }

  const total = sources.reduce((sum, source) => sum + source.clicks, 0);

  return (
    <Table
      head={
        <tr>
          <Th>{t("sources.source")}</Th>
          {showLink && <Th priority="md">{t("sources.link")}</Th>}
          <Th priority="sm">{t("sources.country")}</Th>
          <Th priority="lg">{t("sources.lastSeen")}</Th>
          <Th className="text-right">{t("sources.clicks")}</Th>
        </tr>
      }
    >
      {sources.map((source) => {
        const key = `${source.linkCode}:${source.referrerHost ?? ""}:${source.country ?? ""}`;
        const share = total === 0 ? 0 : source.clicks / total;

        return (
          <tr key={key}>
            <Td>
              <div className="min-w-44 space-y-1">
                {source.referrerHost ? (
                  <Mono className="text-fg">{source.referrerHost}</Mono>
                ) : (
                  /*
                   * Not a failure and not suspicious. It is what a direct
                   * visit, a link opened in an app, and a browser that strips
                   * the header all look like — and for an affiliate who shares
                   * links in a newsletter or a chat, it is most of their
                   * traffic. Naming it honestly beats "unknown", which reads
                   * as something having gone wrong.
                   */
                  <span className="text-[13px] text-fg-muted">{t("sources.direct")}</span>
                )}
                {/* The share as a bar, so ten rows are scannable rather than
                    ten numbers to hold in your head. */}
                <div className="h-1 w-full overflow-hidden rounded-full bg-raised">
                  <div
                    className="h-full rounded-full bg-accent/60"
                    style={{ width: `${Math.max(share > 0 ? 2 : 0, share * 100)}%` }}
                  />
                </div>
              </div>
            </Td>
            {showLink && (
              <Td priority="md">
                <Tag>{source.linkCode}</Tag>
              </Td>
            )}
            <Td priority="sm" className="text-fg-muted">
              {source.country ?? "—"}
            </Td>
            <Td
              priority="lg"
              className="whitespace-nowrap text-fg-muted"
              title={t("sources.firstSeen", { date: formatDate(source.firstAt) })}
            >
              {formatDate(source.lastAt)}
            </Td>
            <Td className="text-right tabular-nums whitespace-nowrap">
              {formatCount(source.clicks)}
              <span className="text-fg-subtle"> · {Math.round(share * 100)}%</span>
            </Td>
          </tr>
        );
      })}
    </Table>
  );
}
